import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { getConsent, recordFirstTouch, setConsent } from "@/lib/analytics";

/**
 * Cookie consent banner · Phase 6.2.
 *
 * Renders only when the user has not yet decided. Decision is persisted in
 * localStorage and propagated to gtag via Consent Mode v2 (see
 * lib/analytics.js → setConsent).
 *
 * Hidden on /admin/* so Mariola never sees it.
 */
export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.location.pathname.startsWith("/admin")) return;
    recordFirstTouch();
    if (!getConsent()) {
      // Wait a beat so we don't pop in during first paint.
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  const decide = (value) => {
    setConsent(value);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Política de cookies"
      data-testid="cookie-banner"
      className="fixed inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-md z-[70] bg-charcoal text-cream rounded-md shadow-2xl border border-gold/20 animate-cookie-in"
      style={{ animation: "cookieIn 320ms ease-out" }}
    >
      <div className="p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="shrink-0 mt-0.5">
            <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
              <Cookie className="h-4 w-4 text-gold" strokeWidth={1.5} />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-gold mb-1">
              Política de cookies
            </p>
            <p className="text-sm leading-relaxed text-cream/90">
              Usamos cookies para entender cómo navegas el sitio y mejorar la experiencia
              editorial. Puedes leer más en nuestra{" "}
              <Link
                to="/privacy"
                onClick={() => setVisible(false)}
                className="underline underline-offset-2 hover:text-gold"
              >
                Política de Privacidad
              </Link>
              .
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                data-testid="cookie-accept"
                onClick={() => decide("granted")}
                className="inline-flex items-center justify-center rounded-full bg-terracotta px-4 py-2 text-xs uppercase tracking-[0.18em] text-cream hover:bg-[#A64826] transition-colors"
              >
                Aceptar
              </button>
              <button
                type="button"
                data-testid="cookie-reject"
                onClick={() => decide("denied")}
                className="inline-flex items-center justify-center rounded-full border border-cream/30 px-4 py-2 text-xs uppercase tracking-[0.18em] text-cream hover:border-gold hover:text-gold transition-colors"
              >
                Rechazar
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setVisible(false)}
            aria-label="Cerrar"
            className="shrink-0 text-cream/45 hover:text-cream transition-colors"
          >
            <X className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>
      </div>
    </div>
  );
}
